import { getObjectValueByPath, getPropertyFromItem } from '@/utils/helpers';

/**
 * If the given value is not an array, wrap it in one.
 *
 * @param  {*} value
 * @return {Array}
 */
export function arrayWrap(value: any): Array<any> {
  return Array.isArray(value) ? value : [value];
}

type ErrorDictionary = {
  [x: string]: any;
};

export class ErrorHandler {
  errors: ErrorDictionary;

  /**
   * Create a new error dictionary instance from a string.
   */
  static fromString(field: string, messages: string) {
    return new this({ [field]: arrayWrap(messages) });
  }

  /**
   * Create a new error dictionary instance.
   */
  constructor(error: ErrorDictionary = {}) {
    // need to default this value
    this.errors = error;
  }

  /**
   *
   * @param {String} field
   * @param {String} messages
   */
  addErrorFromString(field: string, messages: string) {
    this.errors[field] = arrayWrap(messages);
  }

  /**
   * Determine if there is an error for the given field.
   *
   * @param  {String} field
   * @return {Boolean}
   */
  has(field: string): boolean {
    return Boolean(getObjectValueByPath(this.errors, field));
  }

  /**
   * Determine if there are any errors.
   *
   * @return {Boolean}
   */
  any(): boolean {
    return Object.keys(this.errors).length > 0;
  }

  /**
   * Get the first error message for the given field.
   *
   * @param  String} field
   * @return {String|undefined}
   */
  get(field: string): string | undefined {
    if (this.has(field)) {
      return this.getAll(field)[0];
    }
  }

  /**
   * Get all the error messages for the given field.
   *
   * @param  {String} field
   * @return {Array}
   */
  getAll(field: string): Array<any> {
    return arrayWrap(getPropertyFromItem(this.errors, field) || []);
  }

  /**
   * Get all the errors in a flat array.
   *
   * @return {Array}
   */
  flatten(): Array<any> {
    return Object.values(this.errors).reduce((a, b) => a.concat(b), []);
  }

  /**
   * Clear one or all error fields.
   *
   * @param {String?} field
   */
  clear(field?: string) {
    const errors: ErrorDictionary = {};

    if (field) {
      Object.keys(this.errors).forEach(key => {
        if (key !== field) {
          errors[key] = this.errors[key];
        }
      });
    }

    this.errors = errors;
  }

  /**
   * Extract the errors from the response object.
   *
   * @param  {ErrorDictionary} response
   */
  extractErrorFromResponse(response: ErrorDictionary) {
    if (response.status >= 500) {
      this.errors = { error: 'Internal Server Error' };
    }
    if (!response.data || typeof response.data !== 'object') {
      this.errors = { error: ErrorHandler.errorMessage };
    }
    if (response.data.errors) {
      this.errors = { ...response.data.errors };
    }
    if (response.data.detail) {
      this.errors = { error: response.data.detail };
    }
    if (response.data.message) {
      this.errors = { error: response.data.message };
    }
    this.errors = { ...response.data };
  }

  static errorMessage = 'Something went wrong. Please try again.';
}
